import { FieldTypes } from '../../../constants/FieldTypes'
import { notesField } from '../../../fields/shared'
import { MultipleSelectField } from '../../../types'
import { ValidationErrors } from '../../../types/data'
import { FieldMap } from '../../../types/forms/fields'
import { AnyObject } from '../../../types/utils'
import { isFieldMapDataComplete } from '../../../utils/fields'
import { getFieldValue } from '../../../utils/templates/utils'
import { GBS_GA_TRIGGER_DAYS, GBS_WINDOW_END_DAYS } from '../../types/gbsConfig'
import { Encounter, EncounterSection, EncounterSectionType, TypedEncounterSection } from '../types'
import { EncounterType } from '../types/encounterType'
import { admissionSectionField } from './admissionSection'
import {
  assessmentPlanField,
  firstObVisitField,
  generalIllnessExamField,
  labsDiagnosticsField,
  mdmDataReviewedField,
  mdmProblemsAddressedFieldProvider,
  mdmRiskOfManagementField,
  medicationsReviewedField,
  nonRoutinePrenatalExamField,
  prenatalExamField,
  preVisitContactField,
  remoteAssessmentField,
  soapNoteField,
} from './prenatal'
import {
  depressionScreeningField,
  dvIpvScreeningField,
  substanceUseCounselingField,
} from './screenings'
import { hasReviewOfSystemsField, reasonForVisitField, riskAssessmentField } from './shared'
import {
  ultrasoundFindingsField,
  ultrasoundHeaderField,
  ultrasoundScenarioBField,
} from './ultrasound'
import { visitLocationField } from './visitLocation'
import { hasVitalsField, vitalSignsField } from './vitals'

const icd10CodesField: MultipleSelectField = {
  type: FieldTypes.MULTIPLE_SELECT,
  placeholder: 'ICD-10 Diagnosis Codes',
  options: [],
}

const isTelehealthModality = (modality: unknown) =>
  modality === 'telehealth-video' || modality === 'telehealth-audio-only'

// 0 - Not applicable / visible in the form
// 1 - Required
// 2 - Soft required (not required, but shows a warning if not filled and a reason why omitted must be provided)
// 3 - Optional
export type EncounterTier = 0 | 1 | 2 | 3

export type EncounterFieldValidatorType = 'encounter-history' | 'field'
interface BaseEncounterFieldValidator {
  type: EncounterFieldValidatorType
}

// encounter history validators have a function that accepts a list of previous encounters and returns a tier
interface EncounterHistoryFieldValidator extends BaseEncounterFieldValidator {
  type: 'encounter-history'
  validate: (encounters: Encounter[]) => EncounterTier
}

interface FieldFieldValidator extends BaseEncounterFieldValidator {
  type: 'field'
  dependsOn: string[]
  validate: (dependencies: any[]) => EncounterTier
}

export type EncounterFieldValidator = EncounterHistoryFieldValidator | FieldFieldValidator

export interface EncounterSectionTiers {
  [key: string]: EncounterTier | EncounterFieldValidator | EncounterSectionTiers
}

const isEncounterFieldValidator = (
  value: EncounterTier | EncounterFieldValidator | EncounterSectionTiers,
): value is EncounterFieldValidator =>
  typeof value === 'object' &&
  'type' in value &&
  (value.type === 'encounter-history' || value.type === 'field')

export const fieldTiers: Record<EncounterSectionType, EncounterSectionTiers> = {
  field: {},
  // §2.4 + §2.1 Telehealth Tier Adjustments: validators run only when a vitals section is
  // present in the form (routine antepartum only — non-routine uses SOAP note, no vitals section).
  // All validators gate on data.gestationalAge being a number so they safely return Tier 3
  // for non-antepartum encounter types (physical, well-woman) that also include a vitals section.
  vitals: {
    vital: {
      // §2.4: BP is Tier 1 on all routine antepartum in-person visits.
      // §2.1 Telehealth: drops to Tier 2 — not assessable remotely.
      bloodPressure: {
        type: 'field',
        dependsOn: ['data.visitLocation.modality', 'data.gestationalAge'],
        validate: ([modality, ga]: [unknown, unknown]) => {
          if (typeof ga !== 'number') return 3 as EncounterTier
          return (isTelehealthModality(modality) ? 2 : 1) as EncounterTier
        },
      } as EncounterFieldValidator,
      // §2.4: Pulse is Tier 2 on all antepartum visits. Gate on GA to avoid affecting
      // non-antepartum encounter types that also use a vitals section.
      pulse: {
        type: 'field',
        dependsOn: ['data.gestationalAge'],
        validate: ([ga]: [unknown]) =>
          typeof ga === 'number' ? (2 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      // §2.4: Temperature is Tier 2 on all antepartum visits. Same antepartum gate.
      temperature: {
        type: 'field',
        dependsOn: ['data.gestationalAge'],
        validate: ([ga]: [unknown]) =>
          typeof ga === 'number' ? (2 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
    },
    // §2.4: FHT is Tier 1 on routine antepartum in-person visits ≥10w (70d).
    // §2.1 Telehealth: drops to Tier 2 — not assessable remotely.
    fetalHeartTones: {
      type: 'field',
      dependsOn: ['data.visitLocation.modality', 'data.gestationalAge'],
      validate: ([modality, ga]: [unknown, unknown]) => {
        if (typeof ga !== 'number' || ga < 70) return 3 as EncounterTier
        return (isTelehealthModality(modality) ? 2 : 1) as EncounterTier
      },
    } as EncounterFieldValidator,
    // §2.4: Fundal height is validated in the 'prenatal-exam' section against the actual
    // form fields (fundalHeightMeasurement / fundalHeightUnits), gated on the landmark.
    // The legacy `fundalHeightCm` tier was removed: no form writes `data.fundalHeightCm`, so it
    // always read empty and falsely flagged fundal height as incomplete on every ≥20w in-person visit.
    // §2.4: Fetal position hidden <28w (196d); Tier 2 at 28–35w; Tier 1 in-person ≥36w (252d).
    // §2.1 Telehealth: ≥36w drops to Tier 2.
    fetalPosition: {
      type: 'field',
      dependsOn: ['data.visitLocation.modality', 'data.gestationalAge'],
      validate: ([modality, ga]: [unknown, unknown]) => {
        if (typeof ga !== 'number' || ga < 196) return 3 as EncounterTier
        if (ga >= 252) return (isTelehealthModality(modality) ? 2 : 1) as EncounterTier
        return 2 as EncounterTier
      },
    } as EncounterFieldValidator,
  },
  notes: {},
  'review-of-systems': {},
  // §2.8.1: Assessment and Plan are Tier 1 for all visit types. riskAssessment is the
  // current proxy; when the full A&P section is built its fields must also be added here.
  'risk-assessment': { riskAssessment: 1 as EncounterTier },
  // §2.1.4: All four SOAP sections are Tier 1 — blocks authentication if any are blank.
  // Paths match PrenatalExamData.soapNote.* (nested under 'data.soapNote').
  'soap-note': {
    soapNote: {
      subjective: 1 as EncounterTier,
      objective: 1 as EncounterTier,
      assessment: 1 as EncounterTier,
      plan: 1 as EncounterTier,
    },
  },
  // §2.6: All fields Tier 2 (collapsible, optional for non-routine in-person).
  'general-illness-exam': {},
  // §2.1 Non-Routine + Telehealth: all fields optional.
  'remote-assessment': {},
  // §2.1.1: Clinical relationship to pregnancy is Tier 1 for all non-routine visits.
  // This section is only added to non-routine antepartum forms so no GA gate is needed.
  'clinical-rationale': {
    clinicalRelationshipToPregnancy: 1 as EncounterTier,
  },
  // 'pregnancy-header': {},
  // §2.2: Reason for visit is Tier 1 — satisfies E&M baseline documentation requirement.
  // Auto-populated on form open but editable; this tier ensures it can't be cleared before auth.
  'reason-for-visit': {
    reasonForVisit: 1 as EncounterTier,
  },
  allergies: {},
  // §2.5: Medications reviewed is Tier 1 — provider must confirm or edit before authentication.
  'medications-supplements': {
    medicationsReviewed: 1 as EncounterTier,
  },
  // §2.10.1: MDM — problems and risk are Tier 1 (block auth); data reviewed is Tier 2 (soft-required).
  mdm: {
    mdmProblemsAddressed: 1 as EncounterTier,
    mdmRiskOfManagement: 1 as EncounterTier,
    mdmDataReviewed: 2 as EncounterTier,
  },
  // §2.1: Visit modality is Tier 1 for every visit; the location descriptor that identifies where
  // the visit took place is Tier 1 per modality (in-person → location; telehealth → platform +
  // patient/provider location). These mirror the field-level `condition`s in visitLocationField.
  'visit-location': {
    visitLocation: {
      modality: 1 as EncounterTier,
      location: {
        type: 'field',
        dependsOn: ['data.visitLocation.modality'],
        validate: ([modality]: [unknown]) =>
          modality === 'in-person' ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      platform: {
        type: 'field',
        dependsOn: ['data.visitLocation.modality'],
        validate: ([modality]: [unknown]) =>
          isTelehealthModality(modality) ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      patientLocation: {
        type: 'field',
        dependsOn: ['data.visitLocation.modality'],
        validate: ([modality]: [unknown]) =>
          isTelehealthModality(modality) ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      providerLocation: {
        type: 'field',
        dependsOn: ['data.visitLocation.modality'],
        validate: ([modality]: [unknown]) =>
          isTelehealthModality(modality) ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      // §2.12: HIPAA confirmation is Tier 1 when platform = 'other' (free-text platform requires explicit confirmation).
      hipaaComplianceConfirmation: {
        type: 'field',
        dependsOn: ['data.visitLocation.platform', 'data.visitLocation.modality'],
        validate: ([platform, modality]: [unknown, unknown]) =>
          (modality === 'telehealth-video' || modality === 'telehealth-audio-only') &&
          platform === 'other'
            ? (1 as EncounterTier)
            : (3 as EncounterTier),
      } as EncounterFieldValidator,
    },
  },
  // §2.3: All three fields are Tier 1 on the first OB visit, gated on the provider
  // checking `isFirstObVisit`. If unchecked the section fields are hidden and Tier drops to 3.
  'first-ob-visit': {
    firstObVisit: {
      pregnancyConfirmationMethod: {
        type: 'field',
        dependsOn: ['data.firstObVisit.isFirstObVisit'],
        validate: ([isFirstObVisit]: [unknown]) =>
          isFirstObVisit ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      eddEstablishedMethod: {
        type: 'field',
        dependsOn: ['data.firstObVisit.isFirstObVisit'],
        validate: ([isFirstObVisit]: [unknown]) =>
          isFirstObVisit ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      patientHistoryReviewConfirmed: {
        type: 'field',
        dependsOn: ['data.firstObVisit.isFirstObVisit'],
        validate: ([isFirstObVisit]: [unknown]) =>
          isFirstObVisit ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
    },
  },
  // §2.8: assessment, antepartumRisk, and gaConfirmed are Tier 1; returnVisitInterval is Tier 2.
  // antepartumRiskReason is Tier 1 when risk is moderate or high (one-line reason required).
  'assessment-plan': {
    assessment: 1 as EncounterTier,
    antepartumRisk: 1 as EncounterTier,
    gaConfirmed: 1 as EncounterTier,
    returnVisitInterval: 2 as EncounterTier,
    antepartumRiskReason: {
      type: 'field',
      dependsOn: ['data.antepartumRisk'],
      validate: ([risk]: [unknown]) =>
        risk === 'moderate' || risk === 'high' ? (1 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
  },
  // §2.9: At least one ICD-10 code required before authentication.
  'icd10-selection': {
    icd10Codes: 1 as EncounterTier,
  },
  'birthing-center-admission': {
    admittedAt: 1 as EncounterTier,
    admittingMidwife: 1 as EncounterTier,
    admissionType: 1 as EncounterTier,
    // §3.1: Referring provider — Tier 2 when admissionType = Transfer In, Tier 3 otherwise.
    referringProvider: {
      type: 'field',
      dependsOn: ['data.admissionType'],
      validate: ([admissionType]: [unknown]) =>
        admissionType === 'transfer-in' ? (2 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    membraneStatus: 1 as EncounterTier,
    // §3.2: Last oral intake time is Tier 1 — required for any possible surgical intervention.
    lastOralIntakeTime: 1 as EncounterTier,
    // §3.1.1 + §J (Addendum June 2026): GBS acknowledgment gate.
    // Tier 1 when GA ≥ 35w (GBS_GA_TRIGGER_DAYS) and GBS status is unknown/absent.
    // Provider must acknowledge and document a management plan before the admission note
    // can be authenticated. The UI writes gbsStatusAcknowledged = true on the checkbox.
    gbsStatusAcknowledged: {
      type: 'field',
      dependsOn: ['data.gestationalAge', 'data.gbsStatusAtAdmission'],
      validate: ([ga, gbsStatus]: [unknown, unknown]) => {
        if (typeof ga !== 'number' || ga < GBS_GA_TRIGGER_DAYS) return 3 as EncounterTier
        if (!gbsStatus || gbsStatus === 'unknown') return 1 as EncounterTier
        return 3 as EncounterTier
      },
    } as EncounterFieldValidator,
  },
  // §2.6: Physical and pelvic exam cards are optional — no Tier 1 fields.
  // Tier validation handled within the component (cervical membraneStatus).
  'physical-exam': {},
  'pelvic-exam': {},
  // §2.4–2.5: Interval history and fetal exam fields for routine antepartum step 2.
  // Separate from the generic 'field' section type so validators only run for routine antepartum.
  // All validators gate on data.gestationalAge being a number — returns Tier 3 for safety if GA absent.
  'prenatal-exam': {
    // §2.4: Fundal height measurement + units are required only when the fundus is palpable —
    // i.e. a landmark is selected and it is not 'not-yet-palpable'. This mirrors the field-level
    // `condition` on fundalHeightMeasurementField / fundalHeightUnitsField so validation matches
    // the form's conditional visibility (hidden ⇒ not required).
    fundalHeightMeasurement: {
      type: 'field',
      dependsOn: ['data.fundalHeightLandmark'],
      validate: ([landmark]: [unknown]) =>
        landmark && landmark !== 'not-yet-palpable' ? (1 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    fundalHeightUnits: {
      type: 'field',
      dependsOn: ['data.fundalHeightLandmark'],
      validate: ([landmark]: [unknown]) =>
        landmark && landmark !== 'not-yet-palpable' ? (1 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    // §2.5: Fetal movement is Tier 1 at ≥20w (140d).
    fetalMovement: {
      type: 'field',
      dependsOn: ['data.gestationalAge'],
      validate: ([ga]: [unknown]) =>
        typeof ga === 'number' && ga >= 140 ? (1 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    // §2.5: Vaginal bleeding is Tier 1 for all antepartum visits.
    vaginalBleeding: {
      type: 'field',
      dependsOn: ['data.gestationalAge'],
      validate: ([ga]: [unknown]) =>
        typeof ga === 'number' ? (1 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    // §2.5: Contractions is Tier 2 for all antepartum visits.
    contractions: {
      type: 'field',
      dependsOn: ['data.gestationalAge'],
      validate: ([ga]: [unknown]) =>
        typeof ga === 'number' ? (2 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    // §2.5: Leaking fluid is Tier 2 for routine antepartum visits.
    leakingFluid: {
      type: 'field',
      dependsOn: ['data.gestationalAge'],
      validate: ([ga]: [unknown]) =>
        typeof ga === 'number' ? (2 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    // §2.4: Edema is Tier 2 for all antepartum visits.
    edema: {
      type: 'field',
      dependsOn: ['data.gestationalAge'],
      validate: ([ga]: [unknown]) =>
        typeof ga === 'number' ? (2 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    // §2.5: Headache is Tier 2 for all antepartum visits.
    headache: {
      type: 'field',
      dependsOn: ['data.gestationalAge'],
      validate: ([ga]: [unknown]) =>
        typeof ga === 'number' ? (2 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    // §2.5: Visual changes is Tier 2 for all antepartum visits.
    visualChanges: {
      type: 'field',
      dependsOn: ['data.gestationalAge'],
      validate: ([ga]: [unknown]) =>
        typeof ga === 'number' ? (2 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    // §2.5: Epigastric/RUQ pain is Tier 2 for all antepartum visits (preeclampsia/HELLP indicator).
    epigastricPain: {
      type: 'field',
      dependsOn: ['data.gestationalAge'],
      validate: ([ga]: [unknown]) =>
        typeof ga === 'number' ? (2 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    // §2.5: Urinary symptoms is Tier 2 (checkbox with 'None' option — empty = not addressed).
    urinarySymptoms: {
      type: 'field',
      dependsOn: ['data.gestationalAge'],
      validate: ([ga]: [unknown]) =>
        typeof ga === 'number' ? (2 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    // §2.5: Mood/emotional scale is Tier 2 for all antepartum visits.
    moodScale: {
      type: 'field',
      dependsOn: ['data.gestationalAge'],
      validate: ([ga]: [unknown]) =>
        typeof ga === 'number' ? (2 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
  },
  // §2.16: Pre-visit contact — preVisitContactMinutes is Tier 1 only when contactType is set.
  // The section is always present in antepartum forms but entirely optional.
  'pre-visit-contact': {
    timeWorkLog: {
      preVisitContactMinutes: {
        type: 'field',
        dependsOn: ['data.preVisitContact.contactType'],
        validate: ([contactType]: [unknown]) =>
          contactType ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
    },
  },
  // §2.14.1: Indication is Tier 1 for all ultrasound studies.
  'ultrasound-header': {
    indication: 1 as EncounterTier,
  },
  // §2.14.2: Clinical impression Tier 1. Cardiac activity Tier 1 ≥6w (42d); presentation Tier 1 ≥24w (168d).
  // §2.14.3: sonographerSignOff is Tier 1 for sonographer-study scenario only.
  //   data.scenarioType is written to form state on mount by UltrasoundFindingsInput.
  'ultrasound-findings': {
    clinicalImpression: 1 as EncounterTier,
    fetalCardiacActivity: {
      type: 'field',
      dependsOn: ['data.gaAtStudyDays'],
      validate: ([ga]: [unknown]) =>
        typeof ga === 'number' && ga >= 42 ? (1 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
    fetalPresentation: {
      type: 'field',
      dependsOn: ['data.gaAtStudyDays'],
      validate: ([ga]: [unknown]) =>
        typeof ga === 'number' && ga >= 168 ? (1 as EncounterTier) : (2 as EncounterTier),
    } as EncounterFieldValidator,
    sonographerSignOff: {
      type: 'field',
      dependsOn: ['data.scenarioType'],
      validate: ([scenarioType]: [unknown]) =>
        scenarioType === 'sonographer-study' ? (1 as EncounterTier) : (3 as EncounterTier),
    } as EncounterFieldValidator,
  },
  // §2.7 + §J (Addendum June 2026): GBS is Tier 1 at 35–37w GA.
  //   Window corrected from 36–37w (252–265d) to 35–37w (GBS_GA_TRIGGER_DAYS–GBS_WINDOW_END_DAYS).
  //   Both constants are 245 and 265 by default; admin may override via settings/gbsConfig.
  //   validation.ts cannot read Firestore at runtime — it uses the compile-time defaults.
  //   If the admin shifts the trigger, the UI (LabsInput + admission gate) will reflect the
  //   Firestore value immediately; validation.ts will use the default until redeployed.
  // §2.7.1: bloodTypeRh is Tier 2 (soft-required — blood type should be on file).
  //   antibodyScreenOrdered is Tier 1 when Rh-negative AND GA ≥ 28w (196 days).
  //   data.labs.bloodTypeRh is subscribed via LabsInput's useField call.
  'labs-diagnostics': {
    labs: {
      gbsStatus: {
        type: 'field',
        dependsOn: ['data.gestationalAge'],
        validate: ([ga]: [unknown]) =>
          typeof ga === 'number' && ga >= GBS_GA_TRIGGER_DAYS && ga <= GBS_WINDOW_END_DAYS
            ? (1 as EncounterTier)
            : (3 as EncounterTier),
      } as EncounterFieldValidator,
      bloodTypeRh: 2 as EncounterTier,
      antibodyScreenOrdered: {
        type: 'field',
        dependsOn: ['data.labs.bloodTypeRh', 'data.gestationalAge'],
        validate: ([rh, ga]: [unknown, unknown]) =>
          rh === 'negative' && typeof ga === 'number' && ga >= 196
            ? (1 as EncounterTier)
            : (3 as EncounterTier),
      } as EncounterFieldValidator,
    },
  },
  // §2.8.3: DV/IPV Screening tier assignments.
  // screened: TIER 2 at first antepartum visit (no prior encounters) or at 28+ weeks (≥196d GA).
  // Two separate validators combined: encounter-history for first-visit gate, field for 28w gate.
  // result: TIER 1 when screened=true. dismissedReason: TIER 1 when screened=false.
  // Follow-up fields: TIER 1 when concerns identified.
  'dv-ipv-screening': {
    dvIpvScreening: {
      screened: {
        type: 'encounter-history',
        validate: (encounters: Encounter[]) => {
          const priorAntepartum = encounters.filter(e => e.type === EncounterType.ANTEPARTUM)
          // First antepartum visit always TIER 2. Subsequent visits: TIER 3 unless the 28w
          // field validator (below) fires — but field validators run separately from history
          // validators, so the higher tier wins at the UI level.
          return priorAntepartum.length === 0 ? (2 as EncounterTier) : (3 as EncounterTier)
        },
      } as EncounterFieldValidator,
      result: {
        type: 'field',
        dependsOn: ['data.dvIpvScreening.screened'],
        validate: ([screened]: [unknown]) =>
          screened === true ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      dismissedReason: {
        type: 'field',
        dependsOn: ['data.dvIpvScreening.screened'],
        validate: ([screened]: [unknown]) =>
          screened === false ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      followUpPlan: {
        type: 'field',
        dependsOn: ['data.dvIpvScreening.result'],
        validate: ([result]: [unknown]) =>
          result === 'concerns-identified' ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      safetyPlanProvided: {
        type: 'field',
        dependsOn: ['data.dvIpvScreening.result'],
        validate: ([result]: [unknown]) =>
          result === 'concerns-identified' ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      resourcesProvided: {
        type: 'field',
        dependsOn: ['data.dvIpvScreening.result'],
        validate: ([result]: [unknown]) =>
          result === 'concerns-identified' ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      referralMade: {
        type: 'field',
        dependsOn: ['data.dvIpvScreening.result'],
        validate: ([result]: [unknown]) =>
          result === 'concerns-identified' ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
    },
  },
  // §2.8.4: Depression & Anxiety Screening tier assignments.
  // addressed: TIER 1 when gestationalAge is absent (postpartum); TIER 2 when antepartum.
  // notScreenedReason: TIER 1 when addressed=false. followUpPlan: TIER 1 at clinical threshold.
  'depression-screening': {
    depressionScreening: {
      addressed: {
        type: 'field',
        dependsOn: ['data.gestationalAge'],
        validate: ([ga]: [unknown]) =>
          typeof ga !== 'number' ? (1 as EncounterTier) : (2 as EncounterTier),
      } as EncounterFieldValidator,
      notScreenedReason: {
        type: 'field',
        dependsOn: ['data.depressionScreening.addressed'],
        validate: ([addressed]: [unknown]) =>
          addressed === false ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
      followUpPlan: {
        type: 'field',
        dependsOn: ['data.depressionScreening.tool', 'data.depressionScreening.score'],
        validate: ([tool, score]: [unknown, unknown]) => {
          if (typeof score !== 'number') return 3 as EncounterTier
          // EPDS, PHQ-9, GAD-7 all use 10 as the clinical follow-up threshold.
          const threshold = tool === 'EPDS' || tool === 'PHQ-9' || tool === 'GAD-7' ? 10 : 10
          return score >= threshold ? (1 as EncounterTier) : (3 as EncounterTier)
        },
      } as EncounterFieldValidator,
    },
  },
  // §2.8.5: Substance Use & Tobacco Counseling tier assignments.
  // addressed: TIER 2 at first antepartum visit (no prior antepartum encounters); TIER 3 later.
  // dismissedReason: TIER 1 when addressed=false.
  // §2.8.2 (Addendum June 2026): all items TIER 3 — no hard stop
  'patient-education': {},
  'substance-use-counseling': {
    substanceUseCounseling: {
      addressed: {
        type: 'encounter-history',
        validate: (encounters: Encounter[]) => {
          const prior = encounters.filter(e => e.type === EncounterType.ANTEPARTUM)
          return prior.length === 0 ? (2 as EncounterTier) : (3 as EncounterTier)
        },
      } as EncounterFieldValidator,
      dismissedReason: {
        type: 'field',
        dependsOn: ['data.substanceUseCounseling.addressed'],
        validate: ([addressed]: [unknown]) =>
          addressed === false ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
    },
    timeWorkLog: {
      substanceUseCounselingMinutes: {
        type: 'field',
        dependsOn: ['data.substanceUseCounseling.counselingProvided'],
        validate: ([provided]: [unknown]) =>
          provided === true ? (1 as EncounterTier) : (3 as EncounterTier),
      } as EncounterFieldValidator,
    },
  },
}

const evaluateTiers = (
  tiers: EncounterSectionTiers,
  formData: AnyObject,
  encounters: Encounter[],
  prefix = '',
): Array<{ path: string; tier: EncounterTier }> => {
  const results: Array<{ path: string; tier: EncounterTier }> = []
  for (const [key, validator] of Object.entries(tiers)) {
    const path = prefix ? `${prefix}.${key}` : key
    if (typeof validator === 'number') {
      results.push({ path, tier: validator })
    } else if (isEncounterFieldValidator(validator)) {
      if (validator.type === 'encounter-history') {
        results.push({ path, tier: validator.validate(encounters) })
      } else {
        const deps = validator.dependsOn.map(fieldPath => getFieldValue(fieldPath, formData))
        results.push({ path, tier: validator.validate(deps) })
      }
    } else {
      results.push(...evaluateTiers(validator, formData, encounters, path))
    }
  }
  return results
}

const getNestedValue = (obj: AnyObject, path: string): unknown =>
  path.split('.').reduce<unknown>((current, key) => {
    if (current == null || typeof current !== 'object') return undefined
    return (current as AnyObject)[key]
  }, obj)

const isEmptyValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0)

const setNestedError = (errors: AnyObject, path: string, message: string): void => {
  const parts = path.split('.')
  let current = errors
  for (let i = 0; i < parts.length - 1; i++) {
    if (!current[parts[i]]) current[parts[i]] = {}
    current = current[parts[i]] as AnyObject
  }
  current[parts[parts.length - 1]] = message
}

export const isEncounterTier1Complete = (
  sections: EncounterSection[],
  formData: AnyObject,
  encounters: Encounter[],
): boolean => {
  const tierEntries = sections.flatMap(section =>
    evaluateTiers(fieldTiers[section.type], formData, encounters, 'data'),
  )
  return !tierEntries.some(
    ({ path, tier }) => tier === 1 && isEmptyValue(getNestedValue(formData, path)),
  )
}

export const getEncounterValidate = (
  sections: EncounterSection[],
  formData: AnyObject,
  encounters: Encounter[],
): ((data: AnyObject) => ValidationErrors) => {
  const tierEntries = sections.flatMap(section =>
    evaluateTiers(fieldTiers[section.type], formData, encounters, 'data'),
  )

  return data => {
    const errors: AnyObject = {}
    for (const { path, tier } of tierEntries) {
      if (isEmptyValue(getNestedValue(data, path))) {
        if (tier === 1) setNestedError(errors, path, 'Required')
        else if (tier === 2) setNestedError(errors, path, 'soft-required')
      }
    }
    return Object.keys(errors).length > 0 ? errors : undefined
  }
}

export const getSectionValidationField: {
  [K in EncounterSectionType]: (section: TypedEncounterSection<K>) => FieldMap | null
} = {
  field: section => section.field,
  vitals: section => ({
    name: 'Vital Signs',
    children: {
      ...(section.optional ? { hasVitals: hasVitalsField } : {}),
      vital: vitalSignsField,
    },
  }),
  notes: () => ({ name: 'Notes', children: { notes: notesField } }),
  'review-of-systems': () => ({
    name: 'Review of Systems',
    children: { hasReviewOfSystems: hasReviewOfSystemsField },
  }),
  'risk-assessment': () => ({
    name: 'Risk Assessment',
    children: { riskAssessment: riskAssessmentField },
  }),
  'clinical-rationale': () => nonRoutinePrenatalExamField,
  // 'pregnancy-header': () => null,
  'reason-for-visit': () => ({
    name: 'Reason for Visit',
    children: { reasonForVisit: reasonForVisitField },
  }),
  allergies: () => null,
  'medications-supplements': () => ({
    name: 'Medications & Supplements',
    children: { medicationsReviewed: medicationsReviewedField },
  }),
  mdm: () => ({
    name: 'Medical Decision Making',
    children: {
      mdmProblemsAddressed: mdmProblemsAddressedFieldProvider,
      mdmDataReviewed: mdmDataReviewedField,
      mdmRiskOfManagement: mdmRiskOfManagementField,
    },
  }),
  'visit-location': () => ({
    name: 'Visit Location',
    children: { visitLocation: visitLocationField },
  }),
  'first-ob-visit': () => ({
    name: 'First OB Visit',
    children: { firstObVisit: firstObVisitField },
  }),
  'soap-note': () => ({
    name: 'SOAP Note',
    children: { soapNote: soapNoteField },
  }),
  'general-illness-exam': () => ({
    name: 'General Illness Exam',
    children: { generalIllnessExam: generalIllnessExamField },
  }),
  'remote-assessment': () => ({
    name: 'Remote Assessment',
    children: { remoteAssessment: remoteAssessmentField },
  }),
  'assessment-plan': () => ({
    name: 'Assessment & Plan',
    children: { ...assessmentPlanField.children },
  }),
  'icd10-selection': () => ({
    name: 'ICD-10 Selection',
    children: { icd10Codes: icd10CodesField },
  }),
  'birthing-center-admission': () => admissionSectionField,
  'physical-exam': () => null,
  'pelvic-exam': () => null,
  'prenatal-exam': () => prenatalExamField,
  'pre-visit-contact': () => ({
    name: 'Pre-Visit Contact',
    children: { preVisitContact: preVisitContactField },
  }),
  'labs-diagnostics': () => ({
    name: 'Labs & Diagnostics',
    children: { labs: labsDiagnosticsField },
  }),
  'ultrasound-header': () => ({
    name: 'Ultrasound Header',
    children: { ...ultrasoundHeaderField.children },
  }),
  'ultrasound-findings': section => ({
    name: 'Ultrasound Findings',
    children: {
      ...ultrasoundFindingsField.children,
      ...(section.scenario === 'sonographer-study' ? ultrasoundScenarioBField.children : {}),
    },
  }),
  'dv-ipv-screening': () => ({
    name: 'DV/IPV Screening',
    children: { dvIpvScreening: dvIpvScreeningField },
  }),
  'depression-screening': () => ({
    name: 'Depression & Anxiety Screening',
    children: { depressionScreening: depressionScreeningField },
  }),
  'substance-use-counseling': () => ({
    name: 'Substance Use & Tobacco Counseling',
    children: { substanceUseCounseling: substanceUseCounselingField },
  }),
  'patient-education': () => null,
}

// Section-data completeness — used ONLY as the legacy fallback for deciding how many wizard steps to
// reveal (encounters saved before explicit `currentStep` tracking existed). Distinct from the
// tier-based validation above: a section is "complete" when every applicable field in its schema has
// a *defined* value (falsy is fine; `undefined` is not, even for optional fields). `data` is the
// encounter's `data` sub-object (same shape generators receive as formData).
export const isEncounterSectionComplete = (section: EncounterSection, data: AnyObject): boolean => {
  const sectionField = getSectionValidationField[section.type](section as any)
  if (!sectionField) return true
  return isFieldMapDataComplete(sectionField, data)
}
